import microuniversoApi from 'app/api/microuniversoApi'
import { SimulacaoPrecoItemContract } from 'app/types/simulacao-preco/SimulacaoPrecoItemContract'
import { TipoPesquisaContract } from 'app/types/simulacao-preco/TipoPesquisaContract'
import { tratamentoErroPadrao } from 'app/use-cases/tratamentoErroPadrao'
import globalMU from 'globalMU'
import React, { useEffect, useState } from 'react'
import { DadosSimulacao, SimuladorPreco } from './SimuladorPreco'
import * as R from 'ramda'

type SimuladorPrecoContainerProps = {
    identificadorOrcamento: string,
    identificadorItem:number,
    somenteLeitura: boolean
    orcamentoVenda: boolean
    onAplicar: () => void
    onCancelar: () => void
}

const SimuladorPrecoContainer = (props: SimuladorPrecoContainerProps) => {

    const [dadosSimulacao,setDadosSimulacao] = useState<SimulacaoPrecoItemContract>(null)
    const [erro,setErro] = useState<string>(null)
    const [tiposPesquisa,setTiposPesquisa] = useState<TipoPesquisaContract[]>([])
    const [jaCalculado,setJaCalculado] = useState(false)
    const [numeroCasasDecimaisPrecoCompra,setNumeroCasasDecimaisPrecoCompra] = useState(null)
    const [numeroCasasDecimaisPrecoVenda,setNumeroCasasDecimaisPrecoVenda] = useState(null)
    const [carregando,setCarregando] = useState(true)
    const [processando,setProcessando] = useState(true)
    const [permiteEditarMarkup,setPermiteEditarMarkup] = useState(false)

    const atualizarNumeroCasasDecimaisPrecoCompra = async () => {
        const valorParametro = await microuniversoApi.Parametro.obter('NDECPRUNIE')

        setNumeroCasasDecimaisPrecoCompra(
            globalMU.vazioOuNulo(valorParametro) || 
            isNaN(Number(valorParametro)) ||
            Number(valorParametro) < 0 ||
            Number(valorParametro) > 6 ? 2 : Number(valorParametro))
    }

    const atualizarNumeroCasasDecimaisPrecoVenda = async () => {
        const valorParametro = await microuniversoApi.Parametro.obter('CASAS_DECIMAIS_PRECO_VENDA')

        setNumeroCasasDecimaisPrecoVenda(
            globalMU.vazioOuNulo(valorParametro) || 
            isNaN(Number(valorParametro)) ||
            Number(valorParametro) < 0 ||
            Number(valorParametro) > 5 ? 2 : Number(valorParametro))
    }

    const atualizarDadosSimulacao = async () => {
        try {
            const _dadosSimulacao = await microuniversoApi.Orcamento.obterSimulacaoPrecoItem(props.identificadorOrcamento,props.identificadorItem)
            if (_dadosSimulacao == null) {
                globalMU.showWarnMessage("Sem dados para executar simulação de preço")
                props.onCancelar()
            }
            if (("HasErrors" in _dadosSimulacao))
            {
                props.onCancelar()
                return
            }
            const dados = {..._dadosSimulacao, PrecoVendaOriginal: _dadosSimulacao.PrecoVendaInformado, PrecoCustoReal: _dadosSimulacao.PrecoCustoReal}
            setDadosSimulacao(dados)
        } catch (erro) {
            if (erro.Error) {
                globalMU.showWarnMessage(erro.ErrorMessage)
                props.onCancelar()
            } else {
                tratamentoErroPadrao(erro) 
                props.onCancelar()
            }
        }
    }

    const atualizarTiposPesquisa = async () => {
        setTiposPesquisa(
            await R.pipe(
                () => microuniversoApi.TipoPesquisa.listar(),
                R.andThen(tiposPesquisa => R.sort((tp1,tp2) => tp1.Nome.toLowerCase().localeCompare(tp2.Nome.toLowerCase()),tiposPesquisa))
            )()
        )
    }

    const calcularSimulacao = async (dados:DadosSimulacao) => {

        setProcessando(true)
        globalMU.blockUI()

        const novosDadosSimulacao = {
            ...dadosSimulacao,
            PrecoLista: dados.precoLista,
            PrecoCusto: dados.precoCusto,
            PrecoVendaInformado: dados.precoVendaInformado,
            PrecoVendaBaseCustoReal: dados.precoVendaBaseCustoReal,
            PrecoCustoReal: dados.precoCustoReal,
            Composicao: dados.novaComposicao,
            Markup: dados.markup,
            Calculo: dados.calculo,
        } as SimulacaoPrecoItemContract

        try {
            const dadosCalculados = await microuniversoApi.Orcamento.calcularSimulacaoPrecoItem(props.identificadorOrcamento,props.identificadorItem, novosDadosSimulacao)

            setDadosSimulacao(dadosCalculados)

            setJaCalculado(true)
            
            globalMU.showSuccessMessage("Cálculo realizado com sucesso")
        } catch (error) {
            tratamentoErroPadrao(error)
        } finally {
            globalMU.unblockUI()
            setProcessando(false)
        }
    }

    const aplicarSimulacao = async (dados:DadosSimulacao) => {
        setProcessando(true)
        const novosDadosSimulacao = {
            ...dadosSimulacao,
            PrecoLista: dados.precoLista,
            PrecoCusto: dados.precoCusto,
            PrecoVendaInformado: dados.precoVendaInformado,
            PrecoVendaBaseCustoReal: dados.precoVendaBaseCustoReal,
            PrecoCustoReal: dados.precoCustoReal,
            Composicao: dados.novaComposicao,
            Markup: dados.markup,
            Calculo: null,
            PrecoVenda: dados.aplicarPrecoVendaInformado ? dados.precoVendaInformado : dadosSimulacao.PrecoVenda
        } as SimulacaoPrecoItemContract

        globalMU.blockUI()
        try {
            await microuniversoApi.Orcamento.aplicarSimulacaoPrecoItem(props.identificadorOrcamento,props.identificadorItem, novosDadosSimulacao)
            props.onAplicar()

        } catch (error) {
            
            tratamentoErroPadrao(error)
            
        } finally {
            globalMU.unblockUI()
            setProcessando(false)
        }
    }

    const atualizarTodosDados = async () => {
        try {

            setCarregando(true)
            let permiteEditarMarkupParametro = await microuniversoApi.Parametro.obter('VISUALIZA_MARKUP_PRODUTO_ORCAMENTO')=="S"

            setPermiteEditarMarkup(permiteEditarMarkupParametro && props.orcamentoVenda)

            await atualizarDadosSimulacao()
            await atualizarTiposPesquisa()
            
            await atualizarNumeroCasasDecimaisPrecoCompra()
            await atualizarNumeroCasasDecimaisPrecoVenda()
        } finally {
            setCarregando(false)
            setProcessando(false)
        }
    }

    useEffect(() => {
        atualizarTodosDados()
    },[])

    return globalMU.vazioOuNulo(props.identificadorItem) || globalMU.vazioOuNulo(props.identificadorOrcamento) ? 
            <></> : <>
            <SimuladorPreco
                carregandoDados={carregando}
                processando={processando}
                dadosSimulacao={dadosSimulacao}
                tiposPesquisa={tiposPesquisa}
                numeroCasasDecimaisPrecoCompra={numeroCasasDecimaisPrecoCompra}
                numeroCasasDecimaisPrecoVenda={numeroCasasDecimaisPrecoVenda}
                desativarBotaoAplicar={!jaCalculado}
                somenteLeitura={props.somenteLeitura}
                permiteEditarMarkup={permiteEditarMarkup}
                onAplicar={aplicarSimulacao}
                onCalcular={calcularSimulacao}
                onCancelar={props.onCancelar}/>
             
    </>
}

export {SimuladorPrecoContainer,SimuladorPrecoContainerProps}




